import { onBeforeUnmount, onMounted, ref, shallowRef } from "vue";
import { getOverview } from "../api";
import type { Overview } from "../types/api";
import { pulseHistory } from "./useHistory";

/** The collector samples once a minute. */
const SAMPLE_INTERVAL_SECONDS = 60;
/** Wait this long after a sample is due, so the collector has finished writing it. */
const SETTLE_SECONDS = 8;
const RETRY_MS = 30_000;
const MIN_DELAY_MS = 10_000;

export function useDashboard() {
  const data = shallowRef<Overview | null>(null);
  const loading = ref(true);
  const refreshing = ref(false);
  const error = ref<string | null>(null);
  /** Local time of the last successful read and of the next scheduled one, in ms. */
  const refreshedAt = ref(0);
  const nextRefreshAt = ref(0);
  let timer: number | undefined;
  let controller: AbortController | undefined;
  let inFlight = false;
  let skippedWhileHidden = false;

  function scheduleNext(delay: number) {
    if (timer) window.clearTimeout(timer);
    nextRefreshAt.value = Date.now() + delay;
    timer = window.setTimeout(() => {
      // A hidden tab does not need fresh data; catch up as soon as it is looked at again.
      if (document.hidden) {
        skippedWhileHidden = true;
        return;
      }
      void refresh();
    }, delay);
  }

  async function refresh(manual = false) {
    if (inFlight) return;
    inFlight = true;
    controller = new AbortController();
    if (manual || data.value) refreshing.value = true;
    let delay = RETRY_MS;
    try {
      const next = await getOverview(controller.signal);
      data.value = next;
      error.value = null;
      refreshedAt.value = Date.now();
      pulseHistory();
      // Poll just after the next sample lands instead of at an arbitrary phase of the minute.
      // The server-reported age keeps this independent of the viewer's clock.
      const age = next.age_seconds;
      if (age != null && age < SAMPLE_INTERVAL_SECONDS) {
        delay = (SAMPLE_INTERVAL_SECONDS + SETTLE_SECONDS - age) * 1000;
      }
    } catch (reason) {
      if (reason instanceof DOMException && reason.name === "AbortError") return;
      error.value = reason instanceof Error ? reason.message : "暂时无法获取观测数据";
    } finally {
      inFlight = false;
      loading.value = false;
      refreshing.value = false;
    }
    scheduleNext(Math.max(MIN_DELAY_MS, delay));
  }

  function onVisibilityChange() {
    if (document.hidden || !skippedWhileHidden) return;
    skippedWhileHidden = false;
    void refresh();
  }

  onMounted(() => {
    void refresh();
    document.addEventListener("visibilitychange", onVisibilityChange);
  });

  onBeforeUnmount(() => {
    controller?.abort();
    if (timer) window.clearTimeout(timer);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  });

  return { data, loading, refreshing, error, refresh, refreshedAt, nextRefreshAt };
}
